import _ from 'lodash';
import Skeleton from 'react-loading-skeleton';
import { IconButton, IcTarget as icTarget } from '@/shared/ui';
import { isValidHr } from '@/entities/heart-rate';
import useSelectedDisclosure from './useSelectedDisclosure';

export type SelectedDisclosureProps = {
  timezoneOffset?: any;
};

/**
 * Summary card for the ECG disclosure segment the user selected on the HR chart. useSelectedDisclosure supplies the selection and its formatted time span; a skeleton covers chart loading, a placeholder shows while nothing is selected, the channel row is omitted when no channel is set, min, avg and max HR fall back to NA when out of valid range, and the jump-to button scrolls the viewer to the segment.
 *
 * @param props - Card props carrying the timezone offset used to format the disclosure time span.
 * @returns The rendered disclosure summary card, its skeleton, or the empty-selection placeholder.
 */
const SelectedDisclosure = ({ timezoneOffset }: SelectedDisclosureProps) => {
  const { isLoadingHrChart, selectedEcgDisclosure, onClickJumpTo, generateEcgDisclosureTime } =
    useSelectedDisclosure({ timezoneOffset });

  return (
    <div id="ecg-disclosure-info" className="selected-ecg-disclosure-container">
      <div className="header-container">ECG disclosure</div>
      {isLoadingHrChart ? (
        <div className="info-skeleton-container">
          <div className="skeleton-container">
            <Skeleton height="100%" />
          </div>
        </div>
      ) : (
        <div className="info">
          {!_.isEmpty(selectedEcgDisclosure) ? (
            <>
              <div className="left-container">
                <div className="info-item">
                  <div className="title">Time</div>
                  <div className="time">{generateEcgDisclosureTime(selectedEcgDisclosure)}</div>
                </div>
                {!_.isNil(selectedEcgDisclosure.channel) && (
                  <div className="info-item">
                    <div className="title">Channel</div>
                    <div className="time">{`CH${selectedEcgDisclosure.channel}`}</div>
                  </div>
                )}
                <div className="info-item --multi">
                  <div>
                    <div className="title">Min HR</div>
                    {isValidHr(selectedEcgDisclosure.minHr) ? (
                      <strong>{`${selectedEcgDisclosure.minHr} bpm`}</strong>
                    ) : (
                      'NA'
                    )}
                  </div>
                  <div>
                    <div className="title">Avg HR</div>
                    <strong>
                      {isValidHr(selectedEcgDisclosure.avgHr)
                        ? `${selectedEcgDisclosure.avgHr} bpm`
                        : 'NA'}
                    </strong>
                  </div>
                  <div>
                    <div className="title">Max HR</div>
                    {isValidHr(selectedEcgDisclosure.maxHr) ? (
                      <strong>{`${selectedEcgDisclosure.maxHr} bpm`}</strong>
                    ) : (
                      'NA'
                    )}
                  </div>
                </div>
              </div>
              <div className="right-container">
                <IconButton
                  id="jump-to-ecg-disclosure-btn"
                  isFilled
                  iconText="Jump to"
                  iconComponent={<img src={icTarget} alt="Jump to" />}
                  onClick={onClickJumpTo}
                />
              </div>
            </>
          ) : (
            <div>The selected ECG disclosure will appear here.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default SelectedDisclosure;
